// src/admin/screens/loans/actions/RequestRestructureModal.tsx
// Restructuring (extending term, re-scheduling) changes the contract, so it
// requires a second pair of eyes. We insert an admin_action_requests row
// (status=pending, action_type='restructure_loan') and a super admin
// reviews/approves from the action-request queue. The loan itself is NOT
// modified here.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { Json } from '@/lib/database.types';
import type { LoanDetail } from '@admin/hooks/useLoan';

const schema = z.object({
  new_term_days: z.coerce.number().int().min(14).max(180),
  reason: z.string().min(3, 'Reason required'),
});
type Values = z.infer<typeof schema>;

interface Props {
  loan: LoanDetail;
  onClose: (refresh: boolean) => void;
}

export default function RequestRestructureModal({ loan, onClose }: Props) {
  const { admin } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { new_term_days: 60, reason: '' },
  });

  const submit = async (v: Values) => {
    if (!admin) return;
    setBusy(true);
    try {
      const { error } = await supabase.from('admin_action_requests').insert({
        action_type: 'restructure_loan',
        status: 'pending',
        target_loan_id: loan.id,
        request_payload: { new_term_days: v.new_term_days } as Json,
        requested_by: admin.id,
        request_notes: v.reason,
      });
      if (error) throw error;
      await recordAudit({
        action: 'loan.request_restructure',
        target_type: 'loan',
        target_id: loan.id,
        payload: { new_term_days: v.new_term_days, reason: v.reason },
      });
      toast({
        kind: 'info',
        msg: 'Restructure request submitted for super-admin review.',
      });
      onClose(false);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Restructure ${loan.loan_number}`}
    >
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          A super admin (not you) must approve before the loan term changes.
        </p>
        <FormField
          label="New term (days)"
          htmlFor="nt"
          error={errors.new_term_days?.message}
          required
        >
          <input
            id="nt"
            type="number"
            min={14}
            max={180}
            {...register('new_term_days')}
          />
        </FormField>
        <FormField
          label="Reason"
          htmlFor="rsn"
          error={errors.reason?.message}
          required
        >
          <textarea id="rsn" rows={3} {...register('reason')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Submit for review
          </Button>
        </div>
      </form>
    </Modal>
  );
}
